import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, User } from "lucide-react";
import VerifiedBadge from "./VerifiedBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArtistSearchHero() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    let cancelled = false;
    const t = setTimeout(() => {
      const params = new URLSearchParams({ search: q, limit: "8" });
      fetch(`${API}/artists?${params.toString()}`)
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => {
          if (cancelled) return;
          setResults(Array.isArray(data) ? data : []);
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("Artist search failed", err);
          setResults([]);
          setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setQuery("");
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const showDropdown = open && query.trim().length >= 2;

  return (
    <div ref={wrapRef} className="relative w-full max-w-2xl mx-auto mt-6" data-testid="hero-artist-search">
      <div className="relative">
        <Search
          size={20}
          className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
        />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Buscar artista..."
          className="w-full bg-white/[0.07] backdrop-blur-md border border-white/15 rounded-full pl-14 pr-12 py-3.5 sm:py-4 text-white text-base sm:text-lg placeholder:text-zinc-500 focus:bg-white/[0.10] focus:border-[#FF007F]/60 focus:outline-none focus:ring-2 focus:ring-[#FF007F]/20 transition-all shadow-lg shadow-black/30"
          data-testid="hero-artist-search-input"
          aria-label="Buscar artista"
        />
        {query && (
          <button
            onClick={() => { setQuery(""); inputRef.current?.focus(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-zinc-300 hover:text-white transition"
            aria-label="Limpar busca"
            data-testid="hero-artist-search-clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          className="absolute left-0 right-0 mt-2 bg-[#0A0A0A] border border-white/15 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden z-30 max-h-[420px] overflow-y-auto"
          data-testid="hero-artist-search-dropdown"
        >
          {loading && (
            <div className="px-4 py-6 text-center text-zinc-500 text-sm flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-[#FF007F] border-t-transparent rounded-full animate-spin" />
              Buscando...
            </div>
          )}

          {!loading && results.length === 0 && (
            <div className="px-4 py-6 text-center text-zinc-500 text-sm">
              Nenhum artista encontrado para “{query}”.
            </div>
          )}

          {!loading && results.length > 0 && (
            <ul className="divide-y divide-white/5">
              {results.map((a) => (
                <li key={a.id || a.slug}>
                  <Link
                    to={`/artista/${a.slug}`}
                    onClick={() => { setOpen(false); setQuery(""); }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-[#FF007F]/10 transition-colors group"
                    data-testid={`hero-artist-search-result-${a.slug}`}
                  >
                    <div className="w-11 h-11 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0 ring-1 ring-white/10">
                      {a.photo_url ? (
                        <img
                          src={a.photo_url.startsWith("http") ? a.photo_url : `${process.env.REACT_APP_BACKEND_URL}${a.photo_url}`}
                          alt={a.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-zinc-500">
                          <User size={18} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm truncate flex items-center gap-1.5">
                        {a.name}
                        {a.verified && <VerifiedBadge size={12} />}
                      </p>
                      <p className="text-zinc-500 text-xs truncate">
                        {(a.followers_count || 0).toLocaleString("pt-BR")} seguidores
                        {a.genres?.length > 0 && <> · {a.genres.slice(0, 2).join(", ")}</>}
                      </p>
                    </div>
                    <span className="text-[#FF007F] opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold">
                      Ver perfil →
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {!loading && results.length >= 8 && (
            <Link
              to="/artistas"
              onClick={() => { setOpen(false); setQuery(""); }}
              className="block px-4 py-3 text-center text-[#FF007F] text-sm font-bold hover:bg-[#FF007F]/10 transition-colors border-t border-white/5"
              data-testid="hero-artist-search-see-all"
            >
              Ver todos os artistas →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
